import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProducts, submitOrderRequest } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import RimCatalog from './RimCatalog';

// ── Inline Order Modal (same design as ProductsPage) ──────────────────────
const OrderModal = ({ product, onClose }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: user?.name || '', phone: user?.phone || '', email: user?.email || '', quantity: 1, message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitOrderRequest({
        customerName: form.name, phone: form.phone, email: form.email,
        address: user?.address || 'To be confirmed', city: 'To be confirmed',
        subtotal: product.price * form.quantity,
        message: form.message,
        items: [{ name: product.name, brand: product.brand, size: product.size, quantity: Number(form.quantity), price: product.price }]
      });
      setDone(true);
    } catch { alert('Submission failed. Please try again.'); }
    finally { setSubmitting(false); }
  };

  return (
    <div style={ms.overlay} onClick={onClose}>
      <div style={ms.box} onClick={e => e.stopPropagation()}>
        {done ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <p style={{ fontSize: '48px', margin: '0 0 12px' }}>✅</p>
            <h3 style={{ color: '#1a3a5c', margin: '0 0 8px' }}>Order Request Sent!</h3>
            <p style={{ color: '#4a5568', fontSize: '14px', margin: '0 0 20px' }}>Our team will contact you shortly.</p>
            <button style={ms.closeBtn} onClick={onClose}>Close</button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div>
                <p style={{ color: '#c41e3a', fontSize: '10px', fontWeight: 'bold', letterSpacing: '2px', margin: '0 0 4px' }}>ORDER REQUEST</p>
                <h3 style={{ color: '#1a3a5c', margin: 0, fontSize: '15px' }}>{product.brand} {product.name}</h3>
                <p style={{ color: '#4a5568', fontSize: '12px', margin: '3px 0 0' }}>{product.size}{product.mspn ? ` · MSPN #${product.mspn}` : ''}</p>
              </div>
              <button style={ms.xBtn} onClick={onClose}>✕</button>
            </div>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <input style={ms.input} placeholder="Your Full Name *" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              <input style={ms.input} placeholder="Phone Number *" required value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              <input style={ms.input} placeholder="Email (optional)" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <label style={{ color: '#4a5568', fontSize: '13px', whiteSpace: 'nowrap' }}>Quantity:</label>
                <input style={{ ...ms.input, width: '80px' }} type="number" min="1" max="20" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} />
              </div>
              <textarea style={{ ...ms.input, height: '68px', resize: 'vertical' }} placeholder="Notes or questions..." value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} />
              <button type="submit" style={{ ...ms.submitBtn, opacity: submitting ? 0.7 : 1 }} disabled={submitting}>
                {submitting ? 'Sending...' : '📤 Send Order Request'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const ms = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  box: { background: '#fff', borderRadius: '18px', padding: '24px', width: '100%', maxWidth: '420px', boxShadow: '0 24px 60px rgba(0,0,0,0.25)' },
  xBtn: { background: '#f5f7fa', border: 'none', borderRadius: '8px', width: '30px', height: '30px', cursor: 'pointer', fontSize: '15px', color: '#4a5568', flexShrink: 0 },
  input: { width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0', borderRadius: '9px', fontSize: '14px', color: '#1a3a5c', outline: 'none', background: '#f9fafc', boxSizing: 'border-box' },
  submitBtn: { padding: '12px', background: '#c41e3a', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer' },
  closeBtn: { padding: '10px 28px', background: '#1a3a5c', color: '#fff', border: 'none', borderRadius: '9px', cursor: 'pointer', fontWeight: '600' },
};

// ── Main Component ────────────────────────────────────────────────────────
const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderTarget, setOrderTarget] = useState(null);
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await getProducts({ featured: 'true', limit: 12 });
        setProducts(data.products);
      } catch (err) { console.log(err); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const scroll = (dir) => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: dir * 300, behavior: 'smooth' });
  };

  return (
    <>
      {orderTarget && <OrderModal product={orderTarget} onClose={() => setOrderTarget(null)} />}

      {/* ====== FEATURED PRODUCTS CAROUSEL ====== */}
      <div style={styles.section}>
        <div style={styles.container}>
          <p style={styles.tag}>FEATURED PRODUCTS</p>
          <h2 style={styles.title}>Top Picks for You</h2>
          <p style={styles.subtitle}>Hand-picked premium tires and rims from top brands</p>

          {loading ? (
            <div style={styles.loadingWrap}><p style={styles.loading}>Loading products...</p></div>
          ) : products.length === 0 ? (
            <p style={{ color: '#8a9bae', textAlign: 'center', padding: '40px' }}>No featured products yet. Mark products as featured in the admin panel.</p>
          ) : (
            <div style={styles.carouselWrapper}>
              <button style={{ ...styles.arrowBtn, left: 0 }} onClick={() => scroll(-1)} aria-label="Previous">‹</button>
              <div style={styles.carouselTrack} ref={scrollRef}>
                {products.map((product) => (
                  <div key={product._id} style={styles.card}>
                    <div style={styles.imageBox}>
                      {product.isOnSale && <span style={styles.badge}>{product.discountPercent}% OFF</span>}
                      <span style={styles.typeTag}>{product.type === 'tyre' ? 'Tire' : 'Rim'}</span>
                      {product.images && product.images[0] ? (
                        <img src={product.images[0]} alt={product.name} style={styles.productImage} />
                      ) : (
                        <div style={styles.imagePlaceholder}>
                          <span style={styles.placeholderIcon}>{product.type === 'tyre' ? '🔵' : '⭕'}</span>
                        </div>
                      )}
                    </div>
                    <div style={styles.cardBody}>
                      <p style={styles.brand}>{product.brand}</p>
                      <h3 style={styles.name}>{product.name}</h3>
                      <p style={styles.size}>{product.size}{product.speedRating ? ` · ${product.speedRating}` : ''}</p>
                      {product.mspn && <p style={styles.mspn}>MSPN #{product.mspn}</p>}
                      <div style={styles.priceRow}>
                        {product.isOnSale ? (
                          <>
                            <span style={styles.salePrice}>$ {product.discountedPrice?.toLocaleString()}</span>
                            <span style={styles.oldPrice}>$ {product.price?.toLocaleString()}</span>
                          </>
                        ) : (
                          <span style={styles.salePrice}>$ {product.price?.toLocaleString()}</span>
                        )}
                      </div>
                      {/* ORDER NOW — replaces Add to Cart */}
                      <button style={styles.orderBtn} onClick={() => setOrderTarget(product)}>
                        Order Now →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button style={{ ...styles.arrowBtn, right: 0 }} onClick={() => scroll(1)} aria-label="Next">›</button>
            </div>
          )}

          <div style={styles.viewAll}>
            <button style={styles.viewAllBtn}
              onClick={() => navigate('/products')}
              onMouseEnter={e => { e.currentTarget.style.background = '#c41e3a'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#c41e3a'; }}
            >View All Products →</button>
          </div>
        </div>
      </div>

      {/* ====== RIM SIZE CATALOG ====== */}
      <RimCatalog />
    </>
  );
};

const styles = {
  section: { background: '#f5f7fa', padding: '60px 20px 40px' },
  container: { maxWidth: '1200px', margin: '0 auto', textAlign: 'center' },
  tag: { color: '#c41e3a', fontSize: '12px', fontWeight: 'bold', letterSpacing: '3px', marginBottom: '10px' },
  title: { color: '#1a3a5c', fontSize: '34px', fontWeight: 'bold', marginBottom: '10px' },
  subtitle: { color: '#4a5568', fontSize: '15px', marginBottom: '36px' },
  loadingWrap: { padding: '40px 0' },
  loading: { color: '#8a9bae', fontSize: '16px' },
  carouselWrapper: { position: 'relative', padding: '0 48px' },
  arrowBtn: { position: 'absolute', top: '50%', transform: 'translateY(-50%)', background: '#1a3a5c', color: '#fff', border: 'none', borderRadius: '50%', width: '40px', height: '40px', fontSize: '22px', cursor: 'pointer', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(26,58,92,0.25)', transition: 'background 0.2s' },
  carouselTrack: { display: 'flex', gap: '20px', overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', paddingBottom: '8px', textAlign: 'left' },
  card: { background: '#fff', borderRadius: '16px', overflow: 'hidden', border: '1px solid #e2e8f0', minWidth: '240px', maxWidth: '260px', flexShrink: 0, display: 'flex', flexDirection: 'column' },
  imageBox: { height: '170px', background: 'linear-gradient(135deg,#f5f7fa,#eef2f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' },
  badge: { position: 'absolute', top: '10px', left: '10px', background: '#c41e3a', color: '#fff', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold' },
  typeTag: { position: 'absolute', top: '10px', right: '10px', background: '#fff', color: '#4a5568', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', textTransform: 'uppercase', boxShadow: '0 1px 4px rgba(26,58,92,0.1)' },
  imagePlaceholder: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
  placeholderIcon: { fontSize: '56px', opacity: 0.3 },
  productImage: { width: '100%', height: '100%', objectFit: 'cover' },
  cardBody: { padding: '16px', display: 'flex', flexDirection: 'column', flex: 1 },
  brand: { color: '#c41e3a', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '4px' },
  name: { color: '#1a3a5c', fontSize: '15px', fontWeight: '600', marginBottom: '4px' },
  size: { color: '#8a9bae', fontSize: '12px', marginBottom: '2px' },
  mspn: { color: '#4a5568', fontSize: '11px', fontWeight: '600', marginBottom: '8px' },
  priceRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', marginTop: '6px' },
  salePrice: { color: '#1a3a5c', fontSize: '17px', fontWeight: 'bold' },
  oldPrice: { color: '#bbb', fontSize: '12px', textDecoration: 'line-through' },
  orderBtn: { marginTop: 'auto', width: '100%', padding: '9px', background: '#c41e3a', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' },
  viewAll: { marginTop: '32px' },
  viewAllBtn: { padding: '13px 36px', background: 'transparent', color: '#c41e3a', border: '2px solid #c41e3a', borderRadius: '10px', fontSize: '15px', cursor: 'pointer', transition: 'background 0.2s, color 0.2s', fontWeight: '600' },
};

export default Products;